import React from 'react';
import {
  WINDOW_OPTIONS,
  DEFAULT_WINDOW,
  DEFAULT_FILTERS,
  parseTimestamp,
  timestampFromDateAndTime,
  addSeconds,
  secondsBetween,
  windowSeconds,
  chipDate,
  chipTime,
} from '../util/mcpbTime';
import MCCalendar from './MCCalendar';

/**
 * WhenFields — the reusable "WHEN" time-selection block (date · time · show-window), extracted from
 * DtModal (Phase 1c) so the 1g inline-search ENTRY (`EntrySearch`) and the dt-modal share ONE
 * implementation of the time controls (memory: project_mcpb_entry_state_not_mocked — the entry state
 * was never mocked, so it reuses the dt-modal internals rather than re-improvising them).
 *
 * Split into three reusable pieces, all driven by `useWhenState`:
 *   - useWhenState  — owns the working selection (date/h/m/s/window/filters) seeded once from the
 *                     committed search or the NVR clock; exposes the handlers + a derived `value`
 *                     ({ anchorTimestamp, window, filters }) the consumer sends on Go / Show video.
 *   - WhenFields    — presentational date-row + time-row + show-window chips.
 *   - FindEventsChips — the "Find events with" filter chips (client-side narrowing, applied at 1f).
 *
 * Pure/controlled: state lives in the hook so DtModal and EntrySearch each own their own instance and
 * the same field UI renders identically in a modal shell or inline. Anchor = window START; the 10-min
 * lookback is applied later by runMcpbSearch, never here.
 */

const pad2 = (n) => ('0' + n).slice(-2);
const toDateInput = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Zero-padded option lists for the anchor-mode time dropdowns (keyboard-free — a native <select> opens
// the OS wheel on mobile, matching the legacy playback DateContainer pattern).
const HOURS = Array.from({ length: 24 }, (_, i) => pad2(i));
const SIXTY = Array.from({ length: 60 }, (_, i) => pad2(i));

// Clamp an end timestamp to the SAME calendar day as `dayDate` — a custom end can't cross midnight
// (mock rule: "Explicit end on the selected date"). If `addSeconds` rolled past the day, snap to 23:59:59.
const sameDayEnd = (endTs, dayDate) => {
  const dayKey = `${dayDate.getFullYear()}${pad2(dayDate.getMonth() + 1)}${pad2(dayDate.getDate())}`;
  return (!endTs || endTs.slice(0, 8) !== dayKey) ? `${dayKey}235959` : endTs;
};

// Clamp a raw time-input string to a zero-padded 2-digit number within [0, max].
const clampPart = (raw, max) => {
  const n = Math.max(0, Math.min(max, parseInt(raw, 10) || 0));
  return ('0' + n).slice(-2);
};

// Person/Vehicle/Alert use --ai-* color dots; Archived/Marked-for-export use line icons (mock paths).
export const FIND_EVENTS = [
  { key: 'person', label: 'Person', dot: 'var(--ai-person)' },
  { key: 'vehicle', label: 'Vehicle', dot: 'var(--ai-vehicle)' },
  { key: 'alert', label: 'Alert', dot: 'var(--ai-alert)' },
  { key: 'archived', label: 'Archived', path: 'M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5h13.75l.81.97H4.34L5.12 5z' },
  { key: 'export', label: 'Marked for Export', path: 'M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z' },
];

/**
 * useWhenState — working time selection for the WHEN block. Seeds ONCE on mount (= on open) from the
 * committed search, else the NVR clock, else midnight today. `value` is the live derived payload.
 */
export const useWhenState = ({ initial = {}, nvrTimestampShort, anchorOnly = false } = {}) => {
  const seed = parseTimestamp(initial.selectedTimestamp) || parseTimestamp(nvrTimestampShort) || {
    date: new Date(), h: '00', m: '00', s: '00',
  };
  const [workingDate, setWorkingDate] = React.useState(seed.date);
  const [h, setH] = React.useState(seed.h);
  const [m, setM] = React.useState(seed.m);
  const [s, setS] = React.useState(seed.s);

  // Range mode (preset Window vs explicit Custom end — tablet/desktop only; the toggle is CSS-hidden on
  // mobile). A committed CUSTOM search stores its window as a NUMBER (seconds), so re-seeding from that
  // opens straight back into Custom; a preset key (or nothing) opens in Window.
  // Anchor mode (the "Go to time" sheet) sets DATE + START only — the window WIDTH is the ribbon span, and
  // there is no end-time UI. A committed span is stored as a NUMBER (seconds), so without this guard the
  // sheet would open in Custom-range mode and demand an end it can't provide → `valid` stays false and the
  // Search button is stuck disabled (David #86bb7phhh). Custom is a full-mode (DtModal) concept only.
  const initialCustom = !anchorOnly && typeof initial.window === 'number';
  const [rangeMode, setRangeModeRaw] = React.useState(initialCustom ? 'custom' : 'window');
  const [windowKey, setWindowKey] = React.useState(initialCustom ? DEFAULT_WINDOW : (initial.window || DEFAULT_WINDOW));

  // Explicit end (Custom) — h/m/s on the SAME selected date. Seeded from the committed custom end
  // (start + stored seconds) when re-editing one, else a sensible start + preset default (shown only
  // once the user enters Custom).
  const endSeedTs = initialCustom
    ? addSeconds(initial.selectedTimestamp, initial.window)
    : sameDayEnd(addSeconds(timestampFromDateAndTime(seed.date, seed.h, seed.m, seed.s),
      windowSeconds(initial.window || DEFAULT_WINDOW)), seed.date);
  const endSeed = parseTimestamp(endSeedTs) || { h: seed.h, m: seed.m, s: seed.s };
  const [endH, setEndH] = React.useState(endSeed.h);
  const [endM, setEndM] = React.useState(endSeed.m);
  const [endS, setEndS] = React.useState(endSeed.s);

  const [filters, setFilters] = React.useState({ ...DEFAULT_FILTERS, ...(initial.filters || {}) });
  const [showDatePicker, setShowDatePicker] = React.useState(false);

  const stepDay = (delta) => {
    const d = new Date(workingDate);
    d.setDate(d.getDate() + delta);
    setWorkingDate(d);
  };
  const setToYesterday = () => {
    const today = (parseTimestamp(nvrTimestampShort) || {}).date || new Date();
    today.setDate(today.getDate() - 1);
    setWorkingDate(today);
  };
  const fillNow = () => {
    // Prefer the NVR clock string when present. In cloud it's now the SITE-zone "now"
    // (nvrTimestampShort is seeded from JSCustomerProject.sTimezone — #86bbm5p92), so "Now" sets
    // site-local time; the browser-local fallback below only fires when no zone is available at all.
    let now = parseTimestamp(nvrTimestampShort);
    // pad2 to zero-padded strings ("09") so the fallback MATCHES the HOURS/SIXTY <select> option values
    // AND the parseTimestamp path — a raw number (9) selects nothing (regression caught in review).
    if (!now) { const d = new Date(); now = { h: pad2(d.getHours()), m: pad2(d.getMinutes()), s: pad2(d.getSeconds()) }; }
    setH(now.h); setM(now.m); setS(now.s);
  };
  const toggleFilter = (key) => setFilters((f) => ({ ...f, [key]: !f[key] }));
  const pickDate = (value) => {
    const [y, mo, d] = (value || '').split('-').map(Number);
    if (y && mo && d) { setWorkingDate(new Date(y, mo - 1, d)); setShowDatePicker(false); }
  };
  // Set the working date from a 'YYYYMMDD' (the MCCalendar day tap) and collapse the calendar — the
  // anchor-mode counterpart to pickDate's 'YYYY-MM-DD' native-input value.
  const pickYmd = (ymd) => {
    if (ymd && ymd.length >= 8) {
      setWorkingDate(new Date(+ymd.slice(0, 4), +ymd.slice(4, 6) - 1, +ymd.slice(6, 8)));
      setShowDatePicker(false);
    }
  };

  const anchorTimestamp = timestampFromDateAndTime(workingDate, h, m, s);
  const endTimestamp = timestampFromDateAndTime(workingDate, endH, endM, endS);
  const isCustom = rangeMode === 'custom';
  const customWindowSec = secondsBetween(anchorTimestamp, endTimestamp);
  // Window mode is always valid; Custom requires a positive span (end after start).
  const valid = !isCustom || (Number.isFinite(customWindowSec) && customWindowSec > 0);

  // Switching INTO Custom seeds a fresh end = current start + the selected preset (same day), so the
  // default range is always valid and tracks the chosen start instead of a stale mount-time value.
  const setRangeMode = (mode) => {
    if (mode === 'custom' && rangeMode !== 'custom') {
      const e = parseTimestamp(sameDayEnd(addSeconds(anchorTimestamp, windowSeconds(windowKey)), workingDate));
      if (e) { setEndH(e.h); setEndM(e.m); setEndS(e.s); }
    }
    setRangeModeRaw(mode);
  };

  return {
    workingDate, h, m, s, windowKey, filters, showDatePicker,
    rangeMode, endH, endM, endS, valid,
    setH, setM, setS, setWindowKey, setShowDatePicker,
    setEndH, setEndM, setEndS, setRangeMode,
    stepDay, setToYesterday, fillNow, toggleFilter, pickDate, pickYmd,
    // Anchor mode (mobile "Go to time"): the picker commits ONLY the date+start-time — the window WIDTH
    // is the ribbon span (single source), so `value` omits window/filters. Full mode (DtModal) keeps the
    // window preset + filters in the payload as before.
    value: anchorOnly
      ? { anchorTimestamp }
      : { anchorTimestamp, window: isCustom ? customWindowSec : windowKey, filters },
  };
};

/**
 * WhenFields — the WHEN selection block. Two variants driven by one useWhenState instance (`w`):
 *   - default (DtModal, tablet/desktop): date-row + Yesterday/Pick-a-date chips + time row + Show-window
 *     chips + the Window|Custom range toggle. UNCHANGED.
 *   - variant="anchor" (mobile "Go to time" sheet): a single-tap month-grid calendar (green availability
 *     dots) + start-time only. NO Yesterday / Pick-a-date / Show-window — the window WIDTH is the ribbon
 *     span (one source). `onRequestPresence(year, month0)` feeds the calendar dots (omit → no dots).
 */
const WhenFields = ({ w, nvrTimestampShort, variant, onRequestPresence }) => {
  const nvrToday = (parseTimestamp(nvrTimestampShort) || {}).date; // cap "Pick a date" at NVR today
  const workingTs = timestampFromDateAndTime(w.workingDate, w.h, w.m, w.s);

  // ── Anchor variant (mobile) — Java MCCalendarPopup parity. The month calendar is ALWAYS shown (the
  // sheet exists solely to pick a date, so no tap-to-reveal — David 2026-08-04): its ◀ Month ▶ header +
  // the selected-day fill carry the date, and tapping a day selects it. Only DATE + START TIME are set
  // here; the window WIDTH is the ribbon span (one source).
  if (variant === 'anchor') {
    const selectedYmd = workingTs.slice(0, 8); // 'YYYYMMDD'
    const todayYmd = (nvrTimestampShort || '').slice(0, 8);
    return (
      <>
        <MCCalendar
          selectedYmd={selectedYmd}
          todayYmd={todayYmd}
          onPickDay={w.pickYmd}
          onRequestPresence={onRequestPresence}
        />

        {/* Start time only — keyboard-free native <select>s (HH:MM:SS, 24h); the window WIDTH is the ribbon span. */}
        <span className="mcpb-dtp-section-label">Start time</span>
        <div className="mcpb-dtp-time-row">
          <select className="mcpb-dtp-time-select" aria-label="Hour" value={w.h} onChange={(e) => w.setH(e.target.value)}>
            {HOURS.map((hh) => <option key={hh} value={hh}>{hh}</option>)}
          </select>
          <span className="mcpb-dtp-time-sep">:</span>
          <select className="mcpb-dtp-time-select" aria-label="Minute" value={w.m} onChange={(e) => w.setM(e.target.value)}>
            {SIXTY.map((mm) => <option key={mm} value={mm}>{mm}</option>)}
          </select>
          <span className="mcpb-dtp-time-sep">:</span>
          <select className="mcpb-dtp-time-select" aria-label="Second" value={w.s} onChange={(e) => w.setS(e.target.value)}>
            {SIXTY.map((ss) => <option key={ss} value={ss}>{ss}</option>)}
          </select>
          <button className="mcpb-dtp-time-now" type="button" onClick={w.fillNow}>Now</button>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Date */}
      <div className="mcpb-dtp-date-row">
        <button className="mcpb-dtp-date-row__nav" type="button" onClick={() => w.stepDay(-1)} aria-label="Previous day">&lsaquo;</button>
        <div className="mcpb-dtp-date-row__now">{chipDate(workingTs, nvrTimestampShort)}</div>
        <button className="mcpb-dtp-date-row__nav" type="button" onClick={() => w.stepDay(1)} aria-label="Next day">&rsaquo;</button>
      </div>
      <div className="mcpb-dtp-date-chips">
        <button className="mcpb-dtp-date-chip" type="button" onClick={w.setToYesterday}>Yesterday</button>
        <button className="mcpb-dtp-date-chip" type="button" onClick={() => w.setShowDatePicker((v) => !v)}>
          Pick a date&hellip;
        </button>
      </div>
      {w.showDatePicker && (
        <input
          className="mcpb-dtp-datepicker"
          type="date"
          aria-label="Pick a date"
          value={toDateInput(w.workingDate)}
          max={nvrToday ? toDateInput(nvrToday) : undefined}
          onChange={(e) => w.pickDate(e.target.value)}
        />
      )}

      {/* Time */}
      <span className="mcpb-dtp-section-label">Time</span>
      <div className="mcpb-dtp-time-row">
        <input className="mcpb-dtp-time-input" aria-label="Hour" value={w.h} maxLength={2}
          onChange={(e) => w.setH(e.target.value)} onBlur={(e) => w.setH(clampPart(e.target.value, 23))} />
        <span className="mcpb-dtp-time-sep">:</span>
        <input className="mcpb-dtp-time-input" aria-label="Minute" value={w.m} maxLength={2}
          onChange={(e) => w.setM(e.target.value)} onBlur={(e) => w.setM(clampPart(e.target.value, 59))} />
        <span className="mcpb-dtp-time-sep">:</span>
        <input className="mcpb-dtp-time-input" aria-label="Second" value={w.s} maxLength={2}
          onChange={(e) => w.setS(e.target.value)} onBlur={(e) => w.setS(clampPart(e.target.value, 59))} />
        <button className="mcpb-dtp-time-now" type="button" onClick={w.fillNow}>Now</button>
      </div>

      {/* Range — preset Window vs explicit Custom end. Mobile shows only the "Show" label + presets; the
          "Range" label + Window|Custom toggle are CSS-revealed at tablet/desktop (mcplaybackStyle.css),
          matching the mocks (mobile mocks have no toggle). The toggle stays in the DOM at every size, so
          it's never reachable on mobile (display:none) — rangeMode stays 'window' there. */}
      <span className="mcpb-dtp-section-label mcpb-dtp-section-label--show">Show</span>
      <span className="mcpb-dtp-section-label mcpb-dtp-section-label--range">Range</span>
      <div className="mcpb-dtp-range-toggle" role="group" aria-label="Range mode">
        <button
          type="button"
          aria-pressed={w.rangeMode !== 'custom'}
          className={`mcpb-dtp-range-toggle__btn${w.rangeMode !== 'custom' ? ' mcpb-dtp-range-toggle__btn--active' : ''}`}
          onClick={() => w.setRangeMode('window')}
        >
          Window
        </button>
        <button
          type="button"
          aria-pressed={w.rangeMode === 'custom'}
          className={`mcpb-dtp-range-toggle__btn${w.rangeMode === 'custom' ? ' mcpb-dtp-range-toggle__btn--active' : ''}`}
          onClick={() => w.setRangeMode('custom')}
        >
          Custom
        </button>
      </div>

      {w.rangeMode === 'custom' ? (
        <div className="mcpb-dtp-range-custom">
          <div className="mcpb-dtp-time-row">
            <span className="mcpb-dtp-range-custom__to">to</span>
            <input className="mcpb-dtp-time-input" aria-label="End hour" value={w.endH} maxLength={2}
              onChange={(e) => w.setEndH(e.target.value)} onBlur={(e) => w.setEndH(clampPart(e.target.value, 23))} />
            <span className="mcpb-dtp-time-sep">:</span>
            <input className="mcpb-dtp-time-input" aria-label="End minute" value={w.endM} maxLength={2}
              onChange={(e) => w.setEndM(e.target.value)} onBlur={(e) => w.setEndM(clampPart(e.target.value, 59))} />
            <span className="mcpb-dtp-time-sep">:</span>
            <input className="mcpb-dtp-time-input" aria-label="End second" value={w.endS} maxLength={2}
              onChange={(e) => w.setEndS(e.target.value)} onBlur={(e) => w.setEndS(clampPart(e.target.value, 59))} />
          </div>
          {w.valid
            ? <div className="mcpb-dtp-range-custom__note">Explicit end on the selected date &mdash; overrides the window preset.</div>
            : <div className="mcpb-dtp-range-custom__err">End time must be after the start time.</div>}
        </div>
      ) : (
        <div className="mcpb-dtp-window-chips">
          {WINDOW_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              className={`mcpb-dtp-window-chip${w.windowKey === o.key ? ' mcpb-dtp-window-chip--active' : ''}`}
              onClick={() => w.setWindowKey(o.key)}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

/** FindEventsChips — the "Find events with" filter chips. `filters` + `onToggle(key)` from useWhenState. */
export const FindEventsChips = ({ filters, onToggle }) => (
  <div className="mcpb-dtp-event-chips">
    {FIND_EVENTS.map((ev) => (
      <button
        key={ev.key}
        type="button"
        className={`mcpb-dtp-event-chip${filters[ev.key] ? ' mcpb-dtp-event-chip--active' : ''}`}
        aria-pressed={!!filters[ev.key]}
        onClick={() => onToggle(ev.key)}
      >
        {ev.dot
          ? <span className="mcpb-dtp-event-chip__dot" style={{ background: ev.dot }} />
          : <svg viewBox="0 0 24 24" className="mcpb-dtp-event-chip__svg"><path d={ev.path} /></svg>}
        {ev.label}
      </button>
    ))}
  </div>
);

export { chipTime };
export default WhenFields;
